import { useState, useEffect } from "react";
import {
  getSettings,
  updateSettings,
  getExchangeRates,
  refreshExchangeRates,
  type Settings,
  type ExchangeRatesInfo,
} from "../lib/api";

export function SettingsPage() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [exchangeRates, setExchangeRates] = useState<ExchangeRatesInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshingRates, setRefreshingRates] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([
      getSettings(),
      getExchangeRates(),
    ])
      .then(([settingsData, ratesData]) => {
        setSettings(settingsData);
        setExchangeRates(ratesData);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const handleRefreshRates = async () => {
    setRefreshingRates(true);
    try {
      const rates = await refreshExchangeRates();
      setExchangeRates(rates);
      setSuccess("Exchange rates refreshed!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to refresh rates");
    } finally {
      setRefreshingRates(false);
    }
  };

  const formatRatesDate = (fetchedAt: number) => {
    return new Date(fetchedAt).toLocaleString();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!settings) return;

    setError("");
    setSuccess("");
    setSaving(true);

    try {
      const updated = await updateSettings(settings);
      setSettings(updated);
      setSuccess("Settings saved successfully!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const updateField = <K extends keyof Settings>(
    key: K,
    value: Settings[K]
  ) => {
    if (!settings) return;
    setSettings({ ...settings, [key]: value });
  };

  const updateSmtpField = <K extends keyof Settings["smtp"]>(
    key: K,
    value: Settings["smtp"][K]
  ) => {
    if (!settings) return;
    setSettings({
      ...settings,
      smtp: { ...settings.smtp, [key]: value },
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <div className="w-8 h-8 border-2 border-gray-200 border-t-slate-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!settings) {
    return <div className="bg-rose-50 text-rose-800 border border-rose-200 rounded-lg p-4">Failed to load settings</div>;
  }

  return (
    <div className="max-w-xl mx-auto">
      <h2 className="text-xl md:text-2xl font-bold mb-6 md:mb-8">Settings</h2>

      {error && <div className="bg-rose-50 text-rose-800 border border-rose-200 rounded-lg p-4 mb-4">{error}</div>}
      {success && <div className="bg-green-50 text-green-800 border border-green-200 rounded-lg p-4 mb-4">{success}</div>}

      <form onSubmit={handleSubmit}>
        {/* API Key */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6 mb-4 md:mb-6">
          <h3 className="font-semibold mb-4">seats.aero API</h3>

          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium">API Key</label>
            <input
              type="password"
              className="block w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-500/20"
              value={settings.seatsAeroApiKey}
              onChange={(e) => updateField("seatsAeroApiKey", e.target.value)}
              placeholder="Enter your seats.aero API key"
            />
            <p className="mt-1 text-xs text-gray-500">
              Get your API key from{" "}
              <a
                href="https://seats.aero/apikey"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-600 hover:underline"
              >
                seats.aero/apikey
              </a>
            </p>
          </div>

          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium">Poll Interval (minutes)</label>
            <input
              type="number"
              min="1"
              max="60"
              className="w-24 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-500/20"
              value={settings.pollIntervalMinutes}
              onChange={(e) =>
                updateField(
                  "pollIntervalMinutes",
                  parseInt(e.target.value) || 10
                )
              }
            />
            <p className="mt-1 text-xs text-gray-500">
              How often to check for new availability. 1000 API calls/day limit
              means ~1.4 calls/minute max.
            </p>
          </div>

          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium">Exchange Rates</label>
            {exchangeRates ? (
              <div className="text-sm text-gray-600 space-y-1">
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {["CAD", "EUR", "GBP", "AUD", "JPY"].map((currency) => (
                    <span key={currency}>
                      {currency} → USD:{" "}
                      <span className="font-medium">
                        {exchangeRates.rates[currency]?.toFixed(4) || "N/A"}
                      </span>
                    </span>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Rate date: {exchangeRates.date} | Last fetched:{" "}
                  {formatRatesDate(exchangeRates.fetchedAt)}
                </p>
                <button
                  type="button"
                  onClick={handleRefreshRates}
                  disabled={refreshingRates}
                  className="mt-2 inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors cursor-pointer border-none disabled:opacity-50"
                >
                  {refreshingRates ? "Refreshing..." : "Refresh Rates"}
                </button>
              </div>
            ) : (
              <p className="text-sm text-gray-500">Loading exchange rates...</p>
            )}
            <p className="mt-2 text-xs text-gray-500">
              Exchange rates are fetched from European Central Bank and cached for 24 hours.
            </p>
          </div>
        </div>

        {/* SMTP Settings */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6 mb-4 md:mb-6">
          <h3 className="font-semibold mb-4">Email (SMTP)</h3>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2 mb-4">
              <label className="block mb-2 text-sm font-medium">SMTP Host</label>
              <input
                type="text"
                className="block w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-500/20"
                value={settings.smtp.host}
                onChange={(e) => updateSmtpField("host", e.target.value)}
                placeholder="smtp.gmail.com"
              />
            </div>

            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium">Port</label>
              <input
                type="number"
                className="block w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-500/20"
                value={settings.smtp.port}
                onChange={(e) =>
                  updateSmtpField("port", parseInt(e.target.value) || 587)
                }
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium">Username</label>
            <input
              type="text"
              className="block w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-500/20"
              value={settings.smtp.user}
              onChange={(e) => updateSmtpField("user", e.target.value)}
              placeholder="your-email@gmail.com"
            />
          </div>

          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium">Password / App Password</label>
            <input
              type="password"
              className="block w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-500/20"
              value={settings.smtp.pass}
              onChange={(e) => updateSmtpField("pass", e.target.value)}
              placeholder="Enter SMTP password"
            />
          </div>

          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium">From Address</label>
            <input
              type="email"
              className="block w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-500/20"
              value={settings.smtp.from}
              onChange={(e) => updateSmtpField("from", e.target.value)}
              placeholder="alerts@example.com"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              className="w-4 h-4 accent-slate-600"
              checked={settings.smtp.secure}
              onChange={(e) => updateSmtpField("secure", e.target.checked)}
            />
            <label>Use SSL/TLS</label>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Enable for port 465 (implicit TLS). Leave unchecked for port 587 (STARTTLS).
          </p>
        </div>

        {/* Security */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6 mb-4 md:mb-6">
          <h3 className="font-semibold mb-4">Security</h3>

          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium">Access Password</label>
            <input
              type="password"
              className="block w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-500/20"
              value={settings.accessPassword}
              onChange={(e) => updateField("accessPassword", e.target.value)}
              placeholder="Enter new password to change"
            />
            <p className="mt-1 text-xs text-gray-500">
              Password to access this web interface. Leave as "***configured***"
              to keep current password.
            </p>
          </div>
        </div>

        {/* Save */}
        <div className="flex justify-end">
          <button
            type="submit"
            className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg bg-slate-700 text-white hover:bg-slate-800 transition-colors cursor-pointer border-none disabled:opacity-50"
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </form>
    </div>
  );
}
